import { Component, OnInit } from '@angular/core';

import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin, of } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { TipoCampoAdicionalEnum } from '@shared/enums/servico/tipo-campo-adicional.enum';
import { requiredTrim } from '@shared/validators/string-trim.validator';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CampoAdicionalService } from '@shared/services/apis/servico/campo-adicional.service';
import { CampoAdicionalRequest } from '@shared/services/requests/servico/campo-adicional.request';
import { CampoAdicionalOpcaoStruct } from '@shared/services/structs/servico/campo-adicional-opcao.struct';
import { CreateCampoAdicionalResponse } from '@shared/services/responses/servico/create-campo-adicional.response';
import { CampoAdicionalLookupsResponse } from '@shared/services/responses/servico/campo-adicional-lookups.response';
import { CampoAdicionalStruct } from '@shared/services/structs/servico/campo-adicional.struct';
import { TipoCampoAdicional } from '@shared/services/models/servico/tipo-campo-adicional.model';

@Component({
  selector: 'app-campo-adicional-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DragDropModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    LoadingBoxComponent,
    FooterActionComponent,
    HeaderComponent,
  ],
  templateUrl: './campo-adicional-cadastro.component.html',
  styleUrl: './campo-adicional-cadastro.component.css',
})
export class CampoAdicionalCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Servicos;
  public readonly tamanhoMaximoNome = 60;
  public readonly tamanhoMaximoOpcao = 60;
  private readonly quantidadeMinimaOpcoes = 2;

  public form: FormGroup;
  public listaTipoCampoAdicional: TipoCampoAdicional[] = [];
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public isEdicao: boolean = false;
  public idCampoAdicional: number = 0;
  private idTipoCampoAdicionalOriginal: number | null = null;
  public isArrastandoOpcao: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private campoAdicionalService: CampoAdicionalService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.route.params.subscribe(params => {
      if (params['idCampoAdicional']) {
        this.idCampoAdicional = +params['idCampoAdicional'];
        this.isEdicao = true;
      }
      this.carregarDados();
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      nome: ['', [Validators.required, requiredTrim, Validators.maxLength(this.tamanhoMaximoNome)]],
      idTipoCampoAdicional: [TipoCampoAdicionalEnum.Texto, Validators.required],
      isObrigatorioCriacao: [false],
      listaOpcoes: this.formBuilder.array([]),
    });

    this.form.get('idTipoCampoAdicional')?.valueChanges.subscribe(idTipo => {
      if (idTipo === TipoCampoAdicionalEnum.ListaOpcoes && this.opcoes.length === 0) {
        this.adicionarOpcao();
        this.adicionarOpcao();
      }
    });
  }

  public get opcoes(): FormArray {
    return this.form.get('listaOpcoes') as FormArray;
  }

  public get isListaOpcoes(): boolean {
    return this.form.get('idTipoCampoAdicional')?.value === TipoCampoAdicionalEnum.ListaOpcoes;
  }

  private carregarDados(): void {
    this.isLoading = true;
    forkJoin({
      lookups: this.campoAdicionalService.getLookups(),
      campo: this.isEdicao ? this.campoAdicionalService.getById(this.idCampoAdicional) : of(null),
    }).subscribe({
      next: ({ lookups, campo }) => this.tratarDadosCarregados(lookups, campo),
      error: () => {
        this.tratarFalha(
          'Não foi possível carregar o campo adicional agora. Tente novamente em alguns instantes.'
        );
        this.voltar();
      },
    });
  }

  private tratarDadosCarregados(
    lookups: CampoAdicionalLookupsResponse,
    campo: CreateCampoAdicionalResponse | null
  ): void {
    if (lookups.isError) {
      this.tratarFalha(lookups.errorDescription || 'Não foi possível carregar os tipos de campo.');
      this.voltar();
      return;
    }
    if (campo && (campo.isError || !campo.campoAdicional)) {
      this.tratarFalha(campo.errorDescription || 'Não foi possível carregar o campo adicional.');
      this.voltar();
      return;
    }

    this.isLoading = false;
    this.listaTipoCampoAdicional = lookups.listTipoCampoAdicional;
    if (campo) {
      this.preencherFormulario(campo.campoAdicional);
    }
  }

  private preencherFormulario(campoAdicional: CampoAdicionalStruct): void {
    this.opcoes.clear();
    campoAdicional.listaOpcoes.forEach(o => this.adicionarOpcao(o.idCampoAdicionalOpcao, o.descricao));
    this.idTipoCampoAdicionalOriginal = campoAdicional.idTipoCampoAdicional;
    this.form.patchValue({
      nome: campoAdicional.nome,
      idTipoCampoAdicional: campoAdicional.idTipoCampoAdicional,
      isObrigatorioCriacao: campoAdicional.isObrigatorioCriacao,
    });
  }

  public adicionarOpcao(idOpcao: number | null = null, descricao: string = ''): void {
    this.opcoes.push(
      this.formBuilder.group({
        idCampoAdicionalOpcao: [idOpcao],
        descricao: [descricao, Validators.maxLength(this.tamanhoMaximoOpcao)],
      })
    );
  }

  public removerOpcao(indice: number): void {
    this.opcoes.removeAt(indice);
  }

  public reordenarOpcoes(event: CdkDragDrop<FormGroup[]>): void {
    moveItemInArray(this.opcoes.controls, event.previousIndex, event.currentIndex);
    this.opcoes.updateValueAndValidity();
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Aviso', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }

    const request = this.montarRequest();
    if (this.isListaOpcoes && !this.validarOpcoes(request.listaOpcoes)) return;

    if (this.isEdicao && request.idTipoCampoAdicional !== this.idTipoCampoAdicionalOriginal) {
      this.confirmarTrocaTipo(request);
      return;
    }

    this.enviar(request);
  }

  private montarRequest(): CampoAdicionalRequest {
    return {
      nome: this.form.value.nome.trim(),
      idTipoCampoAdicional: this.form.value.idTipoCampoAdicional,
      isObrigatorioCriacao: this.form.value.isObrigatorioCriacao,
      listaOpcoes: this.isListaOpcoes ? this.montarOpcoesPreenchidas() : [],
    };
  }

  private montarOpcoesPreenchidas(): CampoAdicionalOpcaoStruct[] {
    return this.opcoes.value
      .filter((o: CampoAdicionalOpcaoStruct) => o.descricao?.trim())
      .map((o: CampoAdicionalOpcaoStruct) => ({
        idCampoAdicionalOpcao: o.idCampoAdicionalOpcao,
        descricao: o.descricao.trim(),
      }));
  }

  private validarOpcoes(opcoes: CampoAdicionalOpcaoStruct[]): boolean {
    if (opcoes.length < this.quantidadeMinimaOpcoes) {
      this.alertService.show(
        'Aviso',
        `Informe ao menos ${this.quantidadeMinimaOpcoes} opções para a lista.`,
        AlertType.warning
      );
      return false;
    }

    const descricoes = opcoes.map(o => o.descricao.toLowerCase());
    if (new Set(descricoes).size !== descricoes.length) {
      this.alertService.show('Aviso', 'A lista possui opções repetidas.', AlertType.warning);
      return false;
    }
    return true;
  }

  private confirmarTrocaTipo(request: CampoAdicionalRequest): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Alterar o tipo do campo?',
        description: 'O tipo do campo adicional será alterado.',
        warningMessage:
          'Os valores já preenchidos nas ordens de serviço que não forem compatíveis com o novo tipo serão descartados.',
        buttonConfirm: 'Alterar tipo',
        buttonClose: 'Cancelar',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this.enviar(request);
      }
    });
  }

  private enviar(request: CampoAdicionalRequest): void {
    this.isSaving = true;
    const requisicao = this.isEdicao
      ? this.campoAdicionalService.update(this.idCampoAdicional, request)
      : this.campoAdicionalService.create(request);
    requisicao.subscribe({
      next: res => this.tratarRetorno(res),
      error: () =>
        this.tratarFalha(
          'Não foi possível salvar o campo adicional agora. Tente novamente em alguns instantes.'
        ),
    });
  }

  private tratarRetorno(res: CreateCampoAdicionalResponse): void {
    if (res.isError) {
      this.tratarFalha(res.errorDescription || 'Não foi possível salvar o campo adicional.');
      return;
    }

    this.isSaving = false;
    this.alertService.show('Sucesso', 'Campo adicional salvo com sucesso.', AlertType.success);
    this.voltar();
  }

  private tratarFalha(mensagem: string): void {
    this.isLoading = false;
    this.isSaving = false;
    this.alertService.show('Aviso', mensagem, AlertType.warning);
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/servicos/campos-adicionais']);
  }
}
